/*
 * Copyright © 2021-present Protonbase Inc. All rights reserved.
 * The information contained in this software is confidential and proprietary to Protonbase Inc. and its subsidiaries. No part of this software may be used, copied, reproduced, modified, displayed, performed, executed, published, distributed, conveyed, stored, transmitted, or otherwise exploited in any form or by any means, including but not limited to electronic, mechanical, photocopying, recording, transcribing, without the prior written consent of Protonbase Inc.
 * Any violation of the above terms will constitute an infringement of copyright and will be subjected to legal action.
 */
import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { PlatformDataCloud } from 'account-client'
import styles from './index.less'
import { PlatformList } from '@/components/PlatformList'
import { RefreshListButton } from '@/components/PlatformList/RefreshListButton'
import { TPlatformListService } from '@/components/PlatformList/interface'
import { ALL_PAGES, POLL_TIME } from '@/constants'
import { LayoutOthers } from '@/layouts/LayoutOthers'
import { CreateDataCloudModal } from '@/pages/dataCloud/DataClouds/CreateDataCloudModal'
import { DataCloudCard } from '@/pages/dataCloud/DataClouds/DataCloudCard'
import { datacloudIcon, isProtonBase, officialSite } from '@/process.env'
import { accountClient } from '@/services/cloudClient/accountClient'

export function DataClouds() {
  const { t } = useTranslation()
  const [showAlert, setShowAlert] = useState(false)
  const emitter = PlatformList.useCreateEmitter()

  const getDatacloudListService: TPlatformListService = (_, options) => {
    // return Promise.resolve({data: {items: []}})
    return accountClient.datacloudsApi.listDataClouds(ALL_PAGES, options).then(res => {
      setShowAlert(res.data.items.length > 0)
      // Backend returns items in unstable order; sort to keep card order consistent across refreshes
      res.data.items.sort(
        (a: PlatformDataCloud, b: PlatformDataCloud) =>
          (b.gmtCreated ?? 0) - (a.gmtCreated ?? 0) || (a.id ?? '').localeCompare(b.id ?? ''),
      )
      return res
    })
  }

  return (
    <LayoutOthers.MainContent title={t('Data Clouds')}>
      {showAlert && <LayoutOthers.Alert className="margin-bottom-20">{t('data_cloud_desc')}</LayoutOthers.Alert>}
      <PlatformList
        emitter={emitter}
        service={getDatacloudListService}
        pollTime={POLL_TIME.normal}
        createTitle={() => (
          <>
            <RefreshListButton />
            <CreateDataCloudModal />
          </>
        )}
        emptyProps={{
          image: datacloudIcon,
          description: (
            <>
              <div>{t('data_cloud_desc')}</div>
              {isProtonBase && (
                <Trans i18nKey="refer_documentation_desc">
                  Please refer to the{' '}
                  <a href={`${officialSite}/docs/guides/platform/datacloud`} target="_blank">
                    documentation
                  </a>{' '}
                  for more information
                </Trans>
              )}
            </>
          ),
        }}
        createTiles={(datacloudList: PlatformDataCloud[]) => (
          <div className={styles.list}>
            {datacloudList.map(item => (
              <DataCloudCard key={item.id} dataCloud={item} />
            ))}
          </div>
        )}
      />
    </LayoutOthers.MainContent>
  )
}
